import { Box } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import NumberFormat from "react-number-format";
import { motion, AnimatePresence as AnimatePresenceOrig } from "framer-motion";
import { Info, Check, ChevronRight } from "@geist-ui/icons";
import { 
  Text, 
  Card, 
  Grid, 
  Spacer, 
  Divider, 
  useTheme,
  Button,
  Input
} from "@geist-ui/core";

import { InvestmentContextFull } from "../investment-process-full";
import { AppContext } from "../../direct-router";
import { MultistepFormButtonsV2 } from "../../../../components/vertical-multistep-form/multistep-form-buttons";

const AnimatePresence = AnimatePresenceOrig as any;

const PREDEFINED_AMOUNTS = [10000, 25000, 50000, 100000];

const clampValue = (min: number, max: number) => `clamp(${min}rem, 5vw, ${max}rem)`;


export default function InvestmentAmount() {
  const investment_context = useContext(InvestmentContextFull);
  const app_context = useContext(AppContext);
  const theme = useTheme();

  var initial_amount = Number(
    localStorage.getItem(
      `investment-amount-${investment_context.campaign?.cashlinkId}`
    )
  );
  if (initial_amount === 0) initial_amount = 10000;
  
  const [amount, setAmount] = useState(initial_amount);
  const [isCustom, setIsCustom] = useState(!PREDEFINED_AMOUNTS.includes(initial_amount));
  const MIN_AMOUNT = 10000;
  const raw_agio = investment_context.campaign?.agio ?? 0.05;
  const agio_rate = raw_agio >= 1 ? raw_agio / 100 : raw_agio; 
  const [amount_err, setAmountErr] = useState(false);

  useEffect(() => {
    if (amount % 1000 !== 0 || amount < MIN_AMOUNT) {
      setAmountErr(true);
      return;
    }
    setAmountErr(false);
  }, [amount]);

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (amount_err) return;

    console.log("set investment amount", amount);
    localStorage.setItem(
      `investment-amount-${investment_context.campaign?.cashlinkId}`,
      amount.toString()
    );

    investment_context.setLoading(true);

    setTimeout(() => {
      if (amount === investment_context.investment?.amount) {
        console.log("amount didnt change");
        investment_context.setForceRedraw();
      } else {
        investment_context.setAmount(amount);
        if (initial_amount === amount && !app_context.authenticated) {
          investment_context.setForceRedraw();
        }
      }
    }, 1000);
  };

  const calculateTotal = () => {
    const agio = amount * agio_rate;
    return amount + agio;
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4, ease: "easeOut" }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-[800px]"
      >
        <Box mb={5}>
          <Text h2 style={{ 
            fontFamily: '"Nunito Sans", sans-serif', 
            letterSpacing: '-0.02em', 
            fontSize: 'clamp(24px, 5vw, 32px)', 
            fontWeight: 700, 
            marginBottom: '0.75rem',
            lineHeight: 1.2
          }}>
            Anlagebetrag festlegen
          </Text>
          <Text type="secondary" style={{ fontSize: 'clamp(14px, 4vw, 17px)', maxWidth: '600px', lineHeight: 1.6 }}>
            Wählen Sie die gewünschte Investitionssumme für den <span style={{ color: theme.palette.foreground, fontWeight: 500 }}>{investment_context.fund?.name}</span>.
          </Text>
        </Box>

        <Grid.Container gap={2}>
          {PREDEFINED_AMOUNTS.map((prefAmount) => (
            <Grid xs={24} sm={12} key={prefAmount}>
               <div 
                  className="w-full relative group transition-all duration-200"
                  onClick={() => {
                    setAmount(prefAmount);
                    setIsCustom(false);
                  }}
               >
                 <Card 
                    className="transition-all duration-200"
                    style={{ 
                        cursor: 'pointer',
                        borderColor: !isCustom && amount === prefAmount ? '#000' : '#EAEAEA',
                        borderWidth: !isCustom && amount === prefAmount ? '2px' : '1px',
                        boxShadow: 'none',
                        padding: 'clamp(12px, 3vw, 24px) 0',
                        borderRadius: '16px',
                        backgroundColor: '#FFFFFF'
                    }}
                 >
                    <div className="flex justify-between items-center px-1">
                        <Text b style={{ fontSize: 'clamp(1.2rem, 4vw, 1.5rem)', letterSpacing: '-0.03em', margin: 0 }}>
                          {prefAmount.toLocaleString("de-DE")} €
                        </Text>
                        
                        <div className={`w-6 h-6 rounded-full border flex items-center justify-center transition-all ${!isCustom && amount === prefAmount ? 'bg-black border-black' : 'border-gray-200'}`}>
                           {(!isCustom && amount === prefAmount) && <Check size={14} color="white" />}
                        </div>
                    </div>
                 </Card>
               </div>
            </Grid>
          ))}

          <Grid xs={24}>
             <div 
                className="w-full mt-4"
                onClick={() => setIsCustom(true)}
             >
                <Text small style={{ textTransform: 'uppercase', color: theme.palette.accents_5, fontWeight: 600, letterSpacing: '0.05em', marginBottom: '0.75rem', display: 'block', fontSize: '11px' }}>
                   Individueller Betrag
                </Text>
                
                <div 
                   className={`flex items-center border-b transition-colors duration-200 ${isCustom ? 'border-black' : 'border-[#D4D4D4] hover:border-gray-400'}`}
                   style={{ paddingBottom: '0.5rem' }}
                >
                   <NumberFormat
                      className="w-full bg-transparent border-none outline-none font-bold placeholder-gray-300"
                      placeholder="Betrag eingeben"
                      style={{ 
                        fontSize: 'clamp(1.8rem, 6vw, 2.5rem)',
                        letterSpacing: '-0.04em',
                        color: theme.palette.foreground,
                        fontFamily: 'inherit'
                      }}
                      value={amount}
                      suffix=""
                      thousandSeparator="."
                      decimalSeparator=","
                      onValueChange={(values) => {
                          setAmount(Number(values.value));
                      }}
                      onFocus={() => setIsCustom(true)}
                    />
                    <span style={{ fontSize: 'clamp(1.8rem, 6vw, 2.5rem)', fontWeight: 600, color: theme.palette.accents_3, marginLeft: '0.5rem' }}>€</span>
                </div>
                
                <AnimatePresence>
                   {isCustom && amount_err && (
                     <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                       <Text type="error" small style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                         <Info size={14} /> Mindestanlage {MIN_AMOUNT.toLocaleString("de-DE")} €, teilbar durch 1.000 €
                       </Text>
                     </motion.div>
                   )}
                </AnimatePresence>
             </div>
          </Grid>
        </Grid.Container>

        <Spacer h={clampValue(2, 4)} />

        <AnimatePresence>
          {!amount_err && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div style={{ background: '#FFFFFF', border: '1px solid #EAEAEA', borderRadius: '16px', padding: 'clamp(1rem, 3vw, 1.5rem)' }}>
                 <div className="flex justify-between items-center mb-3">
                    <span className="text-gray-500 text-sm">Gewählter Anlagebetrag</span>
                    <span className="font-semibold text-sm sm:text-base">{amount.toLocaleString("de-DE")} €</span>
                 </div>
                 <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-1 text-gray-500 text-sm">
                       <span>Agio ({(agio_rate * 100).toFixed(0)}%)</span>
                       <Info size={12} className="opacity-50" />
                    </div>
                    <span className="text-gray-500 text-sm sm:text-base">{(amount * agio_rate).toLocaleString("de-DE")} €</span>
                 </div>
                 
                 <div className="h-px bg-gray-200 w-full my-4" />
                 
                 <div className="flex justify-between items-end">
                    <span className="font-medium text-base sm:text-lg">Gesamtbetrag</span>
                    <span className="font-bold text-xl sm:text-2xl" style={{ letterSpacing: '-0.03em' }}>{calculateTotal().toLocaleString("de-DE")} €</span>
                 </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <Spacer h={clampValue(2, 4)} />

        <div className="mt-8 sm:mt-12 pt-8 border-t border-gray-100 flex flex-col items-center justify-between gap-8 md:flex-row">
            <div className="flex items-start gap-3 max-w-[440px] w-full">
               <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center border border-gray-200">
                  <Info size={12} className="text-gray-400" />
               </div>
               <Text small style={{ color: theme.palette.accents_5, lineHeight: 1.5, margin: 0, fontSize: '12px' }}>
                 Die Zeichnung ist erst nach Abschluss aller Schritte und Ihrer <span style={{ color: '#000', fontWeight: 600 }}>finalen Bestätigung</span> rechtlich bindend.
               </Text>
            </div>
            
            <div className="w-full md:w-auto">
               <MultistepFormButtonsV2
                   disabled={amount_err}
                   back={false}
                   backFunction={() => null}
                   nextButtonText="Weiter"
               />
            </div>
        </div>
      </motion.div>
    </form>
  );
}
